import { useAnalytics } from "@codat/analytics";
import {
  BinIcon,
  Button,
  DataTable,
  IconButton,
  Pagination,
  Tooltip,
  TooltipContent,
  Typography,
} from "@codat/orchard-ui";
import type {
  ColumnDefinition,
  RowInfo,
} from "@codat/orchard-ui/dist/es/components/DataTable/types";
import { CodatUser } from "api/users";
import classNames from "classnames";
import { usePermissionsContext } from "contexts/PermissionsContext";
import React, { useMemo, useState } from "react";

import { isOnlyAdmin } from "../UserManagement";
import styles from "./UsersTable.module.scss";

type NameCellModel = {
  key: "name";
  value: string;
};
type EmailCellModel = {
  key: "email";
  value: string;
};
type RoleCellModel = {
  key: "role";
  value: string;
};
type EnabledCellModel = {
  value: {
    enabled: boolean;
    id: string;
  };
};

type CtaCellModel = {
  key: "cta";
  value: {
    guid: string;
    deleteDisabled: boolean;
    onEditButtonClick?: () => void;
    onDeleteButtonClick?: () => void;
  };
};

type UserTableRow = [
  NameCellModel,
  EmailCellModel,
  RoleCellModel,
  EnabledCellModel,
  CtaCellModel,
];

const NameCell: React.FC<{ value: string }> = ({ value }) => (
  <Typography className={styles.textCell} data-testid="users-name" as="p">
    {value}
  </Typography>
);

const EmailCell: React.FC<{ value: string }> = ({ value }) => (
  <Typography className={styles.textCell} data-testid="users-email" as="p">
    {value}
  </Typography>
);

const RoleCell: React.FC<{ value: string }> = ({ value }) => (
  <Typography className={styles.textCell} data-testid="users-role" as="p">
    {value}
  </Typography>
);

const EnabledCell: React.FC<EnabledCellModel> = ({ value }) => (
  <div data-testid="users-enabled-status">{value.enabled ? "Yes" : "No"}</div>
);

const CtaCell: React.FC<Pick<CtaCellModel, "value">> = ({ value }) => {
  const { sendEvent } = useAnalytics();
  const [showTooltip, setShowTooltip] = useState(false);
  return (
    <div className={styles.ctaContainer}>
      {value.onEditButtonClick && (
        <Button
          className={styles.editButton}
          size="small"
          variant="secondary"
          label="Edit"
          key={value.guid}
          onClick={() => {
            value.onEditButtonClick?.();
            sendEvent("Edit Button - Click");
          }}
          data-testid="users-table-edit"
        />
      )}
      {value.onDeleteButtonClick && (
        <Tooltip
          isOpen={showTooltip}
          content={
            <TooltipContent>
              You cannot delete the only administrator
            </TooltipContent>
          }
          placement={["right", "left", "top", "bottom"]}
        >
          <IconButton
            label="Delete"
            className={classNames(styles.deleteButton, {
              [styles.disabled]: value.deleteDisabled,
            })}
            onClick={() => {
              if (value.deleteDisabled) return;
              value.onDeleteButtonClick?.();
              sendEvent("Delete Button - Click");
            }}
            data-testid="users-table-delete"
            aria-disabled={value.deleteDisabled}
            onMouseEnter={() => setShowTooltip(value.deleteDisabled)}
            onFocus={() => setShowTooltip(value.deleteDisabled)}
            onMouseLeave={() => setShowTooltip(false)}
            onBlur={() => setShowTooltip(false)}
          >
            <BinIcon />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
};

const columnDefs: ColumnDefinition<string>[] = [
  {
    key: "name",
    label: "Name",
    component: NameCell,
  },
  {
    key: "email",
    label: "Email",
    component: EmailCell,
  },
  {
    key: "role",
    label: "Role",
    component: RoleCell,
  },
  {
    key: "enabled",
    label: "Enabled",
    component: EnabledCell,
  },
  {
    key: "cta",
    label: "",
    component: CtaCell,
    align: "right",
  },
];

interface UsersTableProps {
  users: CodatUser[];
  page: number;
  setPage: (page: number) => void;
  pageSize: number;
  setPageSize: (pageSize: number) => void;
  totalResults: number;
  isSkeleton: boolean;
  onEditUser: (user: CodatUser) => void;
  onDeleteUser: (user: CodatUser) => void;
  disableInteractions?: boolean;
}

export const UsersTable: React.FC<UsersTableProps> = ({
  users,
  page,
  setPage,
  pageSize,
  setPageSize,
  totalResults,
  isSkeleton,
  onEditUser,
  onDeleteUser,
  disableInteractions = false,
}) => {
  const { data: permissions } = usePermissionsContext();
  const { sendEvent } = useAnalytics();

  const tableData = useMemo<RowInfo<UserTableRow>[]>(
    () =>
      users.map((user) => {
        const ctaValue: CtaCellModel["value"] = {
          guid: user.id,
          deleteDisabled: isOnlyAdmin(user, users),
        };

        if (permissions?.includes("Edit") && !disableInteractions) {
          ctaValue.onEditButtonClick = () => onEditUser(user);
        }

        if (
          permissions?.includes("Delete") &&
          !disableInteractions &&
          users.length > 1
        ) {
          ctaValue.onDeleteButtonClick = () => {
            onDeleteUser(user);
          };
        }

        return {
          data: [
            {
              key: "name",
              value: user.name,
            },
            {
              key: "email",
              value: user.email,
            },
            {
              key: "role",
              value: user.role,
            },
            {
              key: "enabled",
              value: {
                enabled: user.enabled,
                id: user.id,
              },
            },
            {
              key: "cta",
              value: ctaValue,
            },
          ],
        };
      }),
    [onEditUser, onDeleteUser, permissions, users, disableInteractions]
  );

  return (
    <>
      <DataTable
        data-testid="users-table"
        columnDefs={columnDefs}
        isSkeleton={isSkeleton}
        rowsInfo={tableData}
        skeletonRows={10}
        className={styles.usersTable}
        isLayoutAuto
        hasBorder
      />
      <Pagination
        label="Number of users to show per page"
        data-testid="users-table-edit"
        onUpdatePageIndex={(page) => {
          setPage(page);
          sendEvent("Page Index Change");
        }}
        onUpdatePerPage={(size) => {
          setPageSize(size);
          sendEvent("Page Size Change", { size });
        }}
        pageIndex={page}
        perPage={pageSize}
        perPageOptions={[10, 20, 50]}
        total={totalResults}
      />
    </>
  );
};
